import React, { useEffect, useState, useRef } from 'react';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { Badge } from '../../ui/Badge';
import { Card } from '../../ui/Card';
import { SearchIcon, PlusIcon, FilterIcon, MoreHorizontalIcon, UserIcon, XIcon, CalendarIcon, CheckCircleIcon, ClockIcon, UsersIcon, TrendingUpIcon, MessageCircleIcon, MapPinIcon, RefreshCwIcon, CheckIcon, BanIcon, UserPlusIcon, EyeIcon, ShoppingCartIcon } from 'lucide-react';
import { attendees, AttendeeData } from '../../../data/attendees';
import { AdminUser, PermissionProfile } from '../../../types/users';
interface NetworkingParticipant {
  id: string;
  userId: string;
  name: string;
  email: string;
  role?: string;
  company?: string;
  participantType: 'attendee' | 'buyer';
  permissions?: PermissionProfile[];
  availability: AvailabilitySlot[];
  createdAt: string;
}
interface AvailabilitySlot {
  date: string;
  startTime: string;
  endTime: string;
  blockedSlots: string[];
}
interface Meeting {
  id: string;
  participant1Id: string;
  participant1Name: string;
  participant2Id: string;
  participant2Name: string;
  date: string;
  startTime: string;
  endTime: string;
  location: string;
  status: 'pending' | 'confirmed' | 'cancelled';
  requestedBy: string;
  createdAt: string;
}
interface NetworkingManagementProps {
  onBack: () => void;
}
// Convert AttendeeData to AdminUser format
const convertAttendeeToUser = (attendee: AttendeeData): AdminUser => {
  // Randomly assign 10% as self-registered
  const isSelfRegistered = Math.random() < 0.1;
  // Determine permissions (users can have multiple)
  const permissions: PermissionProfile[] = [];
  if (isSelfRegistered) {
    permissions.push('general-public');
  } else {
    // All non-self-registered users get B2B access
    permissions.push('b2b-access');
    // Add Buyer permission for buyer roles
    if (attendee.role.toLowerCase().includes('buyer') || attendee.role.toLowerCase().includes('procurement')) {
      permissions.push('buyer');
    }
    // Randomly assign 15% of B2B users as speakers
    if (Math.random() < 0.15) {
      permissions.push('speaker');
    }
  }
  // Special case: Sarah Smith is B2B Access + Buyer + Speaker
  if (attendee.id === 'attendee-0') {
    permissions.length = 0; // Clear existing
    permissions.push('b2b-access', 'buyer', 'speaker');
  }
  // Generate random dates for variety
  const daysAgo = Math.floor(Math.random() * 30);
  const createdDate = new Date();
  createdDate.setDate(createdDate.getDate() - daysAgo - 10);
  const lastLoginDaysAgo = Math.floor(Math.random() * daysAgo);
  const lastLoginDate = new Date();
  lastLoginDate.setDate(lastLoginDate.getDate() - lastLoginDaysAgo);
  // Randomly assign status (mostly active)
  const statusRandom = Math.random();
  const status: 'active' | 'pending' | 'suspended' = statusRandom > 0.95 ? 'suspended' : statusRandom > 0.85 ? 'pending' : 'active';
  // Randomly determine if they've logged in (80% have)
  const hasLoggedIn = Math.random() > 0.2;
  return {
    id: attendee.id,
    email: attendee.email,
    name: attendee.name,
    permissions,
    status,
    registrationSource: isSelfRegistered ? 'self-registered' : 'admin-invited',
    companyId: attendee.source === 'exhibitor' && !isSelfRegistered ? attendee.id : undefined,
    companyName: attendee.source === 'exhibitor' && !isSelfRegistered ? attendee.company : undefined,
    role: attendee.role,
    isExhibiting: attendee.source === 'exhibitor' && !isSelfRegistered,
    standNumber: attendee.source === 'exhibitor' && !isSelfRegistered ? `${String.fromCharCode(65 + Math.floor(Math.random() * 10))}${Math.floor(Math.random() * 99) + 1}` : undefined,
    createdAt: createdDate.toISOString().split('T')[0],
    lastLogin: hasLoggedIn ? lastLoginDate.toISOString().split('T')[0] : undefined,
    eventAccess: attendee.interests.includes('Fitness') ? ['active', 'muscle'] : ['industry'],
    isPrimaryContact: attendee.source === 'exhibitor' && !isSelfRegistered && Math.random() > 0.7,
    interests: attendee.interests
  };
};
export function NetworkingManagement({
  onBack
}: NetworkingManagementProps) {
  const [activeTab, setActiveTab] = useState<'participants' | 'meetings' | 'calendar'>('participants');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [showCalendarView, setShowCalendarView] = useState(false);
  const [showCompareCalendar, setShowCompareCalendar] = useState(false);
  const [activeActionMenu, setActiveActionMenu] = useState<string | null>(null);
  // Filter states
  const [filterType, setFilterType] = useState<'all' | 'attendee' | 'buyer'>('all');
  const [showFiltersDropdown, setShowFiltersDropdown] = useState(false);
  // Add form state
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [participantType, setParticipantType] = useState<'attendee' | 'buyer'>('attendee');
  // Staging area for calendar comparison
  const [stagedParticipants, setStagedParticipants] = useState<string[]>([]);
  // Single calendar view
  const [viewingParticipantId, setViewingParticipantId] = useState<string | null>(null);
  // Calendar view state
  const [calendarSelectedDate, setCalendarSelectedDate] = useState('October 24, 2025');
  const [calendarSelectedSlot, setCalendarSelectedSlot] = useState<string | null>(null);
  const [calendarSelectedMeeting, setCalendarSelectedMeeting] = useState<Meeting | null>(null);
  // Meeting form state
  const [meetingDate, setMeetingDate] = useState('');
  const [meetingStartTime, setMeetingStartTime] = useState('');
  const [meetingEndTime, setMeetingEndTime] = useState('');
  const [meetingLocation, setMeetingLocation] = useState('');
  // Calendar compare state
  const [compareDate, setCompareDate] = useState('October 24, 2025');
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string | null>(null);
  const filtersRef = useRef<HTMLDivElement>(null);
  const actionsMenuRef = useRef<HTMLDivElement>(null);
  const userSearchRef = useRef<HTMLDivElement>(null);
  // Convert attendees to AdminUser format and filter for B2B Access
  const allUsers = attendees.map(convertAttendeeToUser);
  const b2bUsers = allUsers.filter(user => user.permissions.includes('b2b-access'));
  // Use B2B users as available participants
  const availableAttendees = b2bUsers;
  // Mock data - initialize with ALL B2B users as participants
  const [participants, setParticipants] = useState<NetworkingParticipant[]>(() => {
    // Get ALL B2B users as initial participants
    return b2bUsers.map((user, index) => ({
      id: `${index + 1}`,
      userId: user.id,
      name: user.name,
      email: user.email,
      role: user.role,
      company: user.companyName,
      participantType: user.permissions.includes('buyer') ? 'buyer' : 'attendee',
      permissions: user.permissions,
      availability: [{
        date: 'October 24, 2025',
        startTime: '09:00',
        endTime: '17:00',
        blockedSlots: index % 3 === 0 ? ['10:00', '10:30', '15:00', '15:30'] : index % 3 === 1 ? ['11:00', '11:30', '14:00', '14:30'] : ['12:00', '12:30']
      }, {
        date: 'October 25, 2025',
        startTime: '09:00',
        endTime: '17:00',
        blockedSlots: index % 3 === 0 ? ['14:00', '14:30'] : index % 3 === 1 ? ['13:00', '13:30'] : ['11:00', '11:30', '15:00', '15:30']
      }],
      createdAt: user.createdAt
    }));
  });
  const [meetings, setMeetings] = useState<Meeting[]>([{
    id: 'm1',
    participant1Id: '1',
    participant1Name: attendees[0].name,
    participant2Id: '2',
    participant2Name: attendees[1].name,
    date: 'October 24, 2025',
    startTime: '13:00',
    endTime: '13:30',
    location: `${attendees[0].company} Stand`,
    status: 'confirmed',
    requestedBy: '1',
    createdAt: '2025-01-15'
  }]);
  const eventDates = ['October 24, 2025', 'October 25, 2025', 'October 26, 2025'];
  const timeSlots = Array.from({
    length: 18
  }, (_, i) => {
    const hour = Math.floor(i / 2) + 9;
    const minute = i % 2 === 0 ? '00' : '30';
    return `${hour.toString().padStart(2, '0')}:${minute}`;
  });
  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filtersRef.current && !filtersRef.current.contains(event.target as Node)) {
        setShowFiltersDropdown(false);
      }
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(event.target as Node)) {
        setActiveActionMenu(null);
      }
    };
    if (showFiltersDropdown || activeActionMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showFiltersDropdown, activeActionMenu]);
  const filteredUsers = availableAttendees.filter(user => (user.name.toLowerCase().includes(userSearchTerm.toLowerCase()) || user.email.toLowerCase().includes(userSearchTerm.toLowerCase()) || user.company.toLowerCase().includes(userSearchTerm.toLowerCase())) && !participants.some(p => p.userId === user.id));
  const isUserAlreadyParticipant = (userId: string) => {
    return participants.some(p => p.userId === userId);
  };
  const toggleUserSelection = (userId: string) => {
    if (isUserAlreadyParticipant(userId)) return;
    setSelectedUserIds(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
  };
  const handleAddParticipants = () => {
    const newParticipants: NetworkingParticipant[] = selectedUserIds.map(userId => {
      const user = availableAttendees.find(u => u.id === userId)!;
      return {
        id: `participant-${Date.now()}-${userId}`,
        userId: user.id,
        name: user.name,
        email: user.email,
        role: user.role,
        company: user.companyName,
        participantType: user.permissions.includes('buyer') ? 'buyer' : 'attendee',
        permissions: user.permissions,
        availability: [],
        createdAt: new Date().toISOString().split('T')[0]
      };
    });
    setParticipants([...participants, ...newParticipants]);
    setShowAddModal(false);
    setUserSearchTerm('');
    setSelectedUserIds([]);
  };
  const handleAddToStaging = (participantId: string) => {
    if (!stagedParticipants.includes(participantId)) {
      setStagedParticipants([...stagedParticipants, participantId]);
    }
  };
  const handleRemoveFromStaging = (participantId: string) => {
    setStagedParticipants(stagedParticipants.filter(id => id !== participantId));
  };
  const handleViewCalendar = (participantId: string) => {
    setViewingParticipantId(participantId);
    setShowCalendarView(true);
  };
  const handleCompareCalendars = () => {
    if (stagedParticipants.length < 2) return;
    setShowCompareCalendar(true);
  };
  const getSlotAvailability = (date: string, slot: string, participantIds: string[]) => {
    const participantAvailabilities = participantIds.map(id => {
      const participant = participants.find(p => p.id === id);
      return participant?.availability.find(a => a.date === date);
    }).filter(Boolean);
    if (participantAvailabilities.length === 0) return 'unavailable';
    const slotHour = parseInt(slot.split(':')[0]);
    // Check if all participants are within their working hours
    for (const avail of participantAvailabilities) {
      if (!avail) return 'unavailable';
      const startHour = parseInt(avail.startTime.split(':')[0]);
      const endHour = parseInt(avail.endTime.split(':')[0]);
      if (slotHour < startHour || slotHour >= endHour) return 'unavailable';
    }
    // Check if any participant has this slot blocked
    const blockedCount = participantAvailabilities.filter(avail => avail?.blockedSlots?.includes(slot)).length;
    if (blockedCount === participantAvailabilities.length) return 'unavailable';
    if (blockedCount > 0) return 'partial';
    return 'available';
  };
  const handleCreateMeeting = () => {
    if (!selectedTimeSlot || !meetingLocation || stagedParticipants.length < 2) return;
    // For now, create a meeting between first two participants
    // In a real app, you'd handle multi-participant meetings differently
    const p1 = participants.find(p => p.id === stagedParticipants[0])!;
    const p2 = participants.find(p => p.id === stagedParticipants[1])!;
    const endTimeHour = parseInt(selectedTimeSlot.split(':')[0]);
    const endTimeMinute = parseInt(selectedTimeSlot.split(':')[1]);
    const endTime = `${endTimeHour.toString().padStart(2, '0')}:${(endTimeMinute + 30).toString().padStart(2, '0')}`;
    const newMeeting: Meeting = {
      id: `meeting-${Date.now()}`,
      participant1Id: p1.id,
      participant1Name: p1.name,
      participant2Id: p2.id,
      participant2Name: p2.name,
      date: compareDate,
      startTime: selectedTimeSlot,
      endTime,
      location: meetingLocation,
      status: 'confirmed',
      requestedBy: 'admin',
      createdAt: new Date().toISOString().split('T')[0]
    };
    setMeetings([...meetings, newMeeting]);
    setShowCompareCalendar(false);
    setStagedParticipants([]);
    setSelectedTimeSlot(null);
    setMeetingLocation('');
  };
  const handleCancelMeeting = (meetingId: string) => {
    setMeetings(meetings.map(m => m.id === meetingId ? {
      ...m,
      status: 'cancelled' as const
    } : m));
    setActiveActionMenu(null);
  };
  const handleConfirmMeeting = (meetingId: string) => {
    setMeetings(meetings.map(m => m.id === meetingId ? {
      ...m,
      status: 'confirmed' as const
    } : m));
    setActiveActionMenu(null);
  };
  const filteredParticipants = participants.filter(p => {
    const matchesSearch = searchQuery === '' || p.name.toLowerCase().includes(searchQuery.toLowerCase()) || p.email.toLowerCase().includes(searchQuery.toLowerCase()) || p.company?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || p.participantType === filterType;
    return matchesSearch && matchesType;
  });
  return <div className="space-y-6" data-id="element-1886">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-1887">
        <div data-id="element-1888">
          <h1 className="text-2xl font-bold text-gray-900 font-inter" data-id="element-1889">
            Networking Management
          </h1>
          <p className="text-gray-500 font-inter mt-1" data-id="element-1890">
            Manage networking participants, meetings, and availability
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-1891">
        <Card padding="lg" data-id="element-1892">
          <div className="flex items-center justify-between mb-4" data-id="element-1893">
            <div data-id="element-1894">
              <h3 className="text-sm font-medium text-gray-500" data-id="element-1895">
                Total Participants
              </h3>
              <div className="text-3xl font-bold text-gray-900 mt-1" data-id="element-1896">
                {participants.length}
              </div>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg" data-id="element-1897">
              <UsersIcon className="w-6 h-6 text-blue-600" data-id="element-1898" />
            </div>
          </div>
          <p className="text-xs text-gray-500" data-id="element-1899">Attendees & Buyers</p>
        </Card>

        <Card padding="lg" data-id="element-1900">
          <div className="flex items-center justify-between mb-4" data-id="element-1901">
            <div data-id="element-1902">
              <h3 className="text-sm font-medium text-gray-500" data-id="element-1903">Attendees</h3>
              <div className="text-3xl font-bold text-gray-900 mt-1" data-id="element-1904">
                {participants.filter(p => p.participantType === 'attendee').length}
              </div>
            </div>
            <div className="p-3 bg-green-100 rounded-lg" data-id="element-1905">
              <UserIcon className="w-6 h-6 text-green-600" data-id="element-1906" />
            </div>
          </div>
          <p className="text-xs text-gray-500" data-id="element-1907">Active attendees</p>
        </Card>

        <Card padding="lg" data-id="element-1908">
          <div className="flex items-center justify-between mb-4" data-id="element-1909">
            <div data-id="element-1910">
              <h3 className="text-sm font-medium text-gray-500" data-id="element-1911">Buyers</h3>
              <div className="text-3xl font-bold text-gray-900 mt-1" data-id="element-1912">
                {participants.filter(p => p.participantType === 'buyer').length}
              </div>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg" data-id="element-1913">
              <TrendingUpIcon className="w-6 h-6 text-purple-600" data-id="element-1914" />
            </div>
          </div>
          <p className="text-xs text-gray-500" data-id="element-1915">Active buyers</p>
        </Card>

        <Card padding="lg" data-id="element-1916">
          <div className="flex items-center justify-between mb-4" data-id="element-1917">
            <div data-id="element-1918">
              <h3 className="text-sm font-medium text-gray-500" data-id="element-1919">
                Total Meetings
              </h3>
              <div className="text-3xl font-bold text-gray-900 mt-1" data-id="element-1920">
                {meetings.length}
              </div>
            </div>
            <div className="p-3 bg-orange-100 rounded-lg" data-id="element-1921">
              <CalendarIcon className="w-6 h-6 text-orange-600" data-id="element-1922" />
            </div>
          </div>
          <p className="text-xs text-gray-500" data-id="element-1923">
            {meetings.filter(m => m.status === 'confirmed').length} confirmed
          </p>
        </Card>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200" data-id="element-1924">
        <div className="flex gap-8" data-id="element-1925">
          <button onClick={() => setActiveTab('participants')} className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'participants' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-1926">
            Participants ({participants.length})
          </button>
          <button onClick={() => setActiveTab('meetings')} className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'meetings' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-1927">
            Meetings ({meetings.length})
          </button>
        </div>
      </div>

      {/* Participants Tab */}
      {activeTab === 'participants' && <>
          {/* Staging Area */}
          {stagedParticipants.length > 0 && <Card padding="lg" className="bg-blue-50 border-blue-200" data-id="element-1928">
              <div className="flex items-center justify-between mb-4" data-id="element-1929">
                <div data-id="element-1930">
                  <h3 className="text-sm font-semibold text-gray-900" data-id="element-1931">
                    Meeting Participants ({stagedParticipants.length})
                  </h3>
                  <p className="text-xs text-gray-600 mt-1" data-id="element-1932">
                    Add 2 or more participants to compare calendars and schedule
                    a meeting
                  </p>
                </div>
                <div className="flex gap-2" data-id="element-1933">
                  <Button variant="primary" size="sm" onClick={handleCompareCalendars} disabled={stagedParticipants.length < 2} data-id="element-1934">
                    <CalendarIcon className="w-4 h-4 mr-2" data-id="element-1935" />
                    Compare Calendars
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setStagedParticipants([])} data-id="element-1936">
                    Clear All
                  </Button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2" data-id="element-1937">
                {stagedParticipants.map(participantId => {
            const participant = participants.find(p => p.id === participantId);
            if (!participant) return null;
            return <div key={participantId} className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-blue-200" data-id="element-1938">
                      <UserIcon className="w-4 h-4 text-blue-600" data-id="element-1939" />
                      <span className="text-sm font-medium text-gray-900" data-id="element-1940">
                        {participant.name}
                      </span>
                      <button onClick={() => handleRemoveFromStaging(participantId)} className="ml-1 text-gray-400 hover:text-red-600" data-id="element-1941">
                        <XIcon className="w-4 h-4" data-id="element-1942" />
                      </button>
                    </div>;
          })}
              </div>
            </Card>}

          {/* Filters Bar */}
          <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-1943">
            <div className="flex gap-4 items-start" data-id="element-1944">
              <div className="relative flex-1 max-w-md" data-id="element-1945">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-1946" />
                <input type="text" placeholder="Search participants..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-inter text-sm" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1947" />
              </div>

              <div className="flex gap-2" data-id="element-1948">
                <button onClick={() => setFilterType(filterType === 'attendee' ? 'all' : 'attendee')} className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${filterType === 'attendee' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-1949">
                  Attendees
                </button>

                <button onClick={() => setFilterType(filterType === 'buyer' ? 'all' : 'buyer')} className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${filterType === 'buyer' ? 'bg-purple-50 border-purple-200 text-purple-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-1950">
                  Buyers
                </button>
              </div>
            </div>
          </div>

          {/* Participants Table */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm" data-id="element-1951">
            <div className="overflow-x-auto" data-id="element-1952">
              <table className="w-full text-left text-sm font-inter" data-id="element-1953">
                <thead className="bg-gray-50 border-b border-gray-200" data-id="element-1954">
                  <tr data-id="element-1955">
                    <th className="px-6 py-3 font-semibold text-gray-900 min-w-[280px]" data-id="element-1956">
                      Participant
                    </th>
                    <th className="px-6 py-3 font-semibold text-gray-900 min-w-[200px]" data-id="element-1957">
                      Company / Role
                    </th>
                    <th className="px-6 py-3 font-semibold text-gray-900 min-w-[120px]" data-id="element-1958">
                      Type
                    </th>
                    <th className="px-6 py-3 font-semibold text-gray-900 min-w-[140px]" data-id="element-1959">
                      Meetings
                    </th>
                    <th className="px-6 py-3 font-semibold text-gray-900 min-w-[140px]" data-id="element-1960">
                      Last Login
                    </th>
                    <th className="px-6 py-3 font-semibold text-gray-900 text-right min-w-[200px]" data-id="element-1961">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-1962">
                  {filteredParticipants.map(participant => {
                const participantMeetings = meetings.filter(m => m.participant1Id === participant.id || m.participant2Id === participant.id);
                const isStaged = stagedParticipants.includes(participant.id);
                return <tr key={participant.id} onClick={() => handleViewCalendar(participant.id)} className="group hover:bg-gray-50 transition-colors cursor-pointer" data-id="element-1963">
                        <td className="px-6 py-4" data-id="element-1964">
                          <div className="flex items-center gap-3" data-id="element-1965">
                            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center" data-id="element-1966">
                              <UserIcon className="w-5 h-5 text-gray-500" data-id="element-1967" />
                            </div>
                            <div data-id="element-1968">
                              <div className="font-medium text-gray-900" data-id="element-1969">
                                {participant.name}
                              </div>
                              <div className="text-xs text-gray-500" data-id="element-1970">
                                {participant.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4" data-id="element-1971">
                          <div data-id="element-1972">
                            {participant.company && <div className="text-sm font-medium text-gray-900" data-id="element-1973">
                                {participant.company}
                              </div>}
                            {participant.role && <div className="text-xs text-gray-500" data-id="element-1974">
                                {participant.role}
                              </div>}
                            {!participant.company && !participant.role && <span className="text-sm text-gray-400" data-id="element-1975">—</span>}
                          </div>
                        </td>
                        <td className="px-6 py-4" data-id="element-1976">
                          <div className="flex gap-1 flex-wrap" data-id="element-1977">
                            {participant.permissions?.includes('buyer') ? <Badge variant="warning" size="sm" data-id="element-1978">
                                Buyer
                              </Badge> : <Badge variant="success" size="sm" data-id="element-1979">
                                Attendee
                              </Badge>}
                          </div>
                        </td>
                        <td className="px-6 py-4" data-id="element-1980">
                          <div className="flex items-center gap-1.5 text-sm text-gray-600" data-id="element-1981">
                            <CalendarIcon className="w-4 h-4" data-id="element-1982" />
                            {participantMeetings.length} meeting
                            {participantMeetings.length !== 1 ? 's' : ''}
                          </div>
                        </td>
                        <td className="px-6 py-4" data-id="element-1983">
                          <div className="text-sm text-gray-600" data-id="element-1984">
                            {(() => {
                        const user = b2bUsers.find(u => u.id === participant.userId);
                        return user?.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never';
                      })()}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right" data-id="element-1985">
                          <div className="flex items-center justify-end gap-2" data-id="element-1986">
                            {isStaged ? <button onClick={e => {
                        e.stopPropagation();
                        handleRemoveFromStaging(participant.id);
                      }} className="text-gray-600 hover:text-red-600 p-2 rounded hover:bg-red-50 transition-colors" title="Remove from meeting" data-id="element-1987">
                                <XIcon className="w-4 h-4" data-id="element-1988" />
                              </button> : <button onClick={e => {
                        e.stopPropagation();
                        handleAddToStaging(participant.id);
                      }} className="text-gray-600 hover:text-green-600 p-2 rounded hover:bg-green-50 transition-colors" title="Add to meeting" data-id="element-1989">
                                <UserPlusIcon className="w-4 h-4" data-id="element-1990" />
                              </button>}
                          </div>
                        </td>
                      </tr>;
              })}
                </tbody>
              </table>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 text-xs text-gray-500" data-id="element-1991">
              Showing {filteredParticipants.length} of {participants.length}{' '}
              participants
            </div>
          </div>
        </>}

      {/* Meetings Tab */}
      {activeTab === 'meetings' && <>
          <div className="flex justify-between items-center" data-id="element-1992">
            <div className="text-sm text-gray-600" data-id="element-1993">
              {meetings.length} total meeting{meetings.length !== 1 ? 's' : ''}
            </div>
            <Button variant="primary" size="sm" onClick={() => setShowMeetingModal(true)} data-id="element-1994">
              <PlusIcon className="w-4 h-4 mr-2" data-id="element-1995" />
              Schedule Meeting
            </Button>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm" data-id="element-1996">
            <div className="overflow-x-auto" data-id="element-1997">
              <table className="w-full text-left text-sm font-inter" data-id="element-1998">
                <thead className="bg-gray-50 border-b border-gray-200" data-id="element-1999">
                  <tr data-id="element-2000">
                    <th className="px-6 py-3 font-semibold text-gray-900 min-w-[280px]" data-id="element-2001">
                      Participants
                    </th>
                    <th className="px-6 py-3 font-semibold text-gray-900 min-w-[180px]" data-id="element-2002">
                      Date & Time
                    </th>
                    <th className="px-6 py-3 font-semibold text-gray-900 min-w-[160px]" data-id="element-2003">
                      Location
                    </th>
                    <th className="px-6 py-3 font-semibold text-gray-900 min-w-[120px]" data-id="element-2004">
                      Status
                    </th>
                    <th className="px-6 py-3 font-semibold text-gray-900 text-right min-w-[80px]" data-id="element-2005">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-2006">
                  {meetings.map(meeting => <tr key={meeting.id} className="group hover:bg-gray-50 transition-colors" data-id="element-2007">
                      <td className="px-6 py-4" data-id="element-2008">
                        <div className="space-y-1" data-id="element-2009">
                          <div className="font-medium text-gray-900" data-id="element-2010">
                            {meeting.participant1Name}
                          </div>
                          <div className="text-sm text-gray-600" data-id="element-2011">
                            with {meeting.participant2Name}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-2012">
                        <div className="space-y-1" data-id="element-2013">
                          <div className="text-sm text-gray-900" data-id="element-2014">
                            {new Date(meeting.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                      })}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-2015">
                            {meeting.startTime} - {meeting.endTime}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-2016">
                        <div className="flex items-center gap-1.5 text-sm text-gray-600" data-id="element-2017">
                          <MapPinIcon className="w-4 h-4" data-id="element-2018" />
                          {meeting.location}
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-2019">
                        <Badge variant={meeting.status === 'confirmed' ? 'success' : meeting.status === 'pending' ? 'warning' : 'default'} size="sm" data-id="element-2020">
                          {meeting.status}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-right" data-id="element-2021">
                        <div className="relative" data-id="element-2022">
                          <button data-meeting-id={meeting.id} onClick={() => setActiveActionMenu(activeActionMenu === meeting.id ? null : meeting.id)} className="text-gray-400 hover:text-gray-600" data-id="element-2023">
                            <MoreHorizontalIcon className="w-5 h-5" data-id="element-2024" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            {/* Actions Menu */}
            {activeActionMenu && <div className="fixed inset-0 z-50" onClick={() => setActiveActionMenu(null)} data-id="element-2025">
                {meetings.map(meeting => {
            if (activeActionMenu !== meeting.id) return null;
            return <div key={meeting.id} ref={actionsMenuRef} className="absolute w-48 bg-white rounded-lg shadow-lg border border-gray-200" style={{
              top: `${document.querySelector(`[data-meeting-id="${meeting.id}"]`)?.getBoundingClientRect().bottom || 0}px`,
              right: '2rem'
            }} onClick={e => e.stopPropagation()} data-id="element-2026">
                      {meeting.status === 'pending' && <button onClick={() => handleConfirmMeeting(meeting.id)} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 border-b border-gray-100" data-id="element-2027">
                          <CheckIcon className="w-4 h-4" data-id="element-2028" />
                          Confirm Meeting
                        </button>}
                      <button onClick={() => handleCancelMeeting(meeting.id)} className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2" data-id="element-2029">
                        <BanIcon className="w-4 h-4" data-id="element-2030" />
                        Cancel Meeting
                      </button>
                    </div>;
          })}
              </div>}

            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 text-xs text-gray-500" data-id="element-2031">
              Showing {meetings.length} meeting
              {meetings.length !== 1 ? 's' : ''}
            </div>
          </div>
        </>}

      {/* Add Participants Modal */}
      {showAddModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-2032">
          <Card className="w-full max-w-2xl" data-id="element-2033">
            <div className="p-6 max-h-[80vh] flex flex-col" data-id="element-2034">
              <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-2035">
                Add Networking Participants
              </h3>
              <p className="text-sm text-gray-500 mb-6" data-id="element-2036">
                Select app users to add as networking participants
              </p>

              {/* Participant Type Selection */}
              <div className="mb-4" data-id="element-2037">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2038">
                  Participant Type
                </label>
                <div className="flex gap-3" data-id="element-2039">
                  <button onClick={() => setParticipantType('attendee')} className={`flex-1 p-3 rounded-lg border-2 transition-all ${participantType === 'attendee' ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2040">
                    <div className="font-semibold" data-id="element-2041">Attendee</div>
                    <div className="text-xs mt-1" data-id="element-2042">
                      General networking participant
                    </div>
                  </button>
                  <button onClick={() => setParticipantType('buyer')} className={`flex-1 p-3 rounded-lg border-2 transition-all ${participantType === 'buyer' ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2043">
                    <div className="font-semibold" data-id="element-2044">Buyer</div>
                    <div className="text-xs mt-1" data-id="element-2045">
                      Procurement/purchasing role
                    </div>
                  </button>
                </div>
              </div>

              {/* Search */}
              <div className="mb-4" ref={userSearchRef} data-id="element-2046">
                <div className="relative" data-id="element-2047">
                  <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-2048" />
                  <input type="text" placeholder="Search by name or email..." value={userSearchTerm} onChange={e => setUserSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" data-id="element-2049" />
                </div>
              </div>

              {/* Selected Count */}
              {selectedUserIds.length > 0 && <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-2050">
                  <div className="flex items-center justify-between" data-id="element-2051">
                    <span className="text-sm font-medium text-blue-900" data-id="element-2052">
                      {selectedUserIds.length} user
                      {selectedUserIds.length !== 1 ? 's' : ''} selected
                    </span>
                    <button onClick={() => setSelectedUserIds([])} className="text-xs text-blue-600 hover:text-blue-700 font-medium" data-id="element-2053">
                      Clear selection
                    </button>
                  </div>
                </div>}

              {/* User List */}
              <div className="flex-1 overflow-y-auto border border-gray-200 rounded-lg" data-id="element-2054">
                {filteredUsers.length === 0 ? <div className="p-8 text-center text-gray-500" data-id="element-2055">
                    <UserIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" data-id="element-2056" />
                    <p className="text-sm" data-id="element-2057">No users found</p>
                  </div> : <div className="divide-y divide-gray-200" data-id="element-2058">
                    {filteredUsers.map(user => {
                const isAlreadyParticipant = isUserAlreadyParticipant(user.id);
                const isSelected = selectedUserIds.includes(user.id);
                return <label key={user.id} className={`flex items-center gap-3 p-4 transition-colors ${isAlreadyParticipant ? 'bg-gray-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'}`} data-id="element-2059">
                          <input type="checkbox" checked={isSelected} onChange={() => toggleUserSelection(user.id)} disabled={isAlreadyParticipant} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2060" />
                          {user.profileImage ? <img src={user.profileImage} alt={user.name} className="w-10 h-10 rounded-full object-cover flex-shrink-0" data-id="element-2061" /> : <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-2062">
                              <UserIcon className="w-5 h-5 text-gray-500" data-id="element-2063" />
                            </div>}
                          <div className="flex-1 min-w-0" data-id="element-2064">
                            <div className="flex items-center gap-2" data-id="element-2065">
                              <div className="text-sm font-medium text-gray-900 truncate" data-id="element-2066">
                                {user.name}
                              </div>
                              {isAlreadyParticipant && <Badge variant="success" size="sm" data-id="element-2067">
                                  <CheckCircleIcon className="w-3 h-3 mr-1" data-id="element-2068" />
                                  Already Added
                                </Badge>}
                            </div>
                            <div className="text-xs text-gray-500 truncate" data-id="element-2069">
                              {user.role} at {user.company}
                            </div>
                            <div className="text-xs text-gray-400 truncate" data-id="element-2070">
                              {user.email}
                            </div>
                          </div>
                        </label>;
              })}
                  </div>}
              </div>

              {/* Actions */}
              <div className="flex gap-3 mt-6" data-id="element-2071">
                <Button variant="outline" onClick={() => {
              setShowAddModal(false);
              setSelectedUserIds([]);
              setUserSearchTerm('');
            }} className="flex-1" data-id="element-2072">
                  Cancel
                </Button>
                <Button variant="primary" className="flex-1" onClick={handleAddParticipants} disabled={selectedUserIds.length === 0} data-id="element-2073">
                  <CheckCircleIcon className="w-4 h-4 mr-2" data-id="element-2074" />
                  Add{' '}
                  {selectedUserIds.length > 0 ? `${selectedUserIds.length} ` : ''}
                  Participant
                  {selectedUserIds.length !== 1 ? 's' : ''}
                </Button>
              </div>
            </div>
          </Card>
        </div>}

      {/* Schedule Meeting Modal */}
      {showMeetingModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-2075">
          <Card className="w-full max-w-lg" data-id="element-2076">
            <div className="p-6" data-id="element-2077">
              <h3 className="text-lg font-bold text-gray-900 mb-6" data-id="element-2078">
                Schedule Meeting
              </h3>
              <div className="space-y-4" data-id="element-2079">
                <div data-id="element-2080">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2081">
                    Participant 1
                  </label>
                  <select value={selectedParticipant1} onChange={e => setSelectedParticipant1(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" data-id="element-2082">
                    <option value="" data-id="element-2083">Select participant...</option>
                    {participants.map(p => <option key={p.id} value={p.id} data-id="element-2084">
                        {p.name} ({p.participantType})
                      </option>)}
                  </select>
                </div>

                <div data-id="element-2085">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2086">
                    Participant 2
                  </label>
                  <select value={selectedParticipant2} onChange={e => setSelectedParticipant2(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" data-id="element-2087">
                    <option value="" data-id="element-2088">Select participant...</option>
                    {participants.filter(p => p.id !== selectedParticipant1).map(p => <option key={p.id} value={p.id} data-id="element-2089">
                          {p.name} ({p.participantType})
                        </option>)}
                  </select>
                </div>

                <div data-id="element-2090">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2091">
                    Date
                  </label>
                  <select value={meetingDate} onChange={e => setMeetingDate(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" data-id="element-2092">
                    <option value="" data-id="element-2093">Select date...</option>
                    {eventDates.map(date => <option key={date} value={date} data-id="element-2094">
                        {date}
                      </option>)}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4" data-id="element-2095">
                  <div data-id="element-2096">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2097">
                      Start Time
                    </label>
                    <select value={meetingStartTime} onChange={e => setMeetingStartTime(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" data-id="element-2098">
                      <option value="" data-id="element-2099">Select time...</option>
                      {timeSlots.map(slot => <option key={slot} value={slot} data-id="element-2100">
                          {slot}
                        </option>)}
                    </select>
                  </div>
                  <div data-id="element-2101">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2102">
                      End Time
                    </label>
                    <select value={meetingEndTime} onChange={e => setMeetingEndTime(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" data-id="element-2103">
                      <option value="" data-id="element-2104">Select time...</option>
                      {timeSlots.map(slot => <option key={slot} value={slot} data-id="element-2105">
                          {slot}
                        </option>)}
                    </select>
                  </div>
                </div>

                <div data-id="element-2106">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2107">
                    Location
                  </label>
                  <Input type="text" placeholder="e.g., Stand A12, Meeting Room 1" value={meetingLocation} onChange={e => setMeetingLocation(e.target.value)} data-id="element-2108" />
                </div>
              </div>

              <div className="flex gap-3 mt-6" data-id="element-2109">
                <Button variant="outline" onClick={() => {
              setShowMeetingModal(false);
              setSelectedParticipant1('');
              setSelectedParticipant2('');
              setMeetingDate('');
              setMeetingStartTime('');
              setMeetingEndTime('');
              setMeetingLocation('');
            }} className="flex-1" data-id="element-2110">
                  Cancel
                </Button>
                <Button variant="primary" className="flex-1" onClick={handleCreateMeeting} disabled={!selectedParticipant1 || !selectedParticipant2 || !meetingDate || !meetingStartTime || !meetingEndTime || !meetingLocation} data-id="element-2111">
                  <CalendarIcon className="w-4 h-4 mr-2" data-id="element-2112" />
                  Schedule Meeting
                </Button>
              </div>
            </div>
          </Card>
        </div>}

      {/* Individual Calendar View Modal */}
      {showCalendarView && viewingParticipantId && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-2113">
          <Card className="w-full max-w-6xl" data-id="element-2114">
            <div className="p-6 max-h-[85vh] overflow-y-auto" data-id="element-2115">
              {(() => {
            const participant = participants.find(p => p.id === viewingParticipantId);
            if (!participant) return null;
            // Mock meetings for this participant
            const participantMeetings = meetings.filter(m => m.participant1Id === participant.id || m.participant2Id === participant.id);
            const getMeetingsForDate = (date: string) => {
              return participantMeetings.filter(m => m.date === date);
            };
            const getSlotMeeting = (date: string, slot: string) => {
              return participantMeetings.find(m => m.date === date && m.startTime === slot);
            };
            return <>
                    <div className="flex items-center justify-between mb-6" data-id="element-2116">
                      <div data-id="element-2117">
                        <h3 className="text-lg font-bold text-gray-900" data-id="element-2118">
                          {participant.name}'s Calendar
                        </h3>
                        <p className="text-sm text-gray-600 mt-1" data-id="element-2119">
                          {participant.role} at {participant.company}
                        </p>
                      </div>
                      <button onClick={() => {
                  setShowCalendarView(false);
                  setViewingParticipantId(null);
                  setCalendarSelectedDate('October 24, 2025');
                  setCalendarSelectedSlot(null);
                  setCalendarSelectedMeeting(null);
                }} className="text-gray-400 hover:text-gray-600" data-id="element-2120">
                        <XIcon className="w-6 h-6" data-id="element-2121" />
                      </button>
                    </div>

                    <div className="space-y-6" data-id="element-2122">
                      {/* Date Selector */}
                      <div data-id="element-2123">
                        <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-2124">
                          Select Date
                        </label>
                        <div className="grid grid-cols-3 gap-3" data-id="element-2125">
                          {eventDates.map(date => <button key={date} onClick={() => {
                      setCalendarSelectedDate(date);
                      setCalendarSelectedSlot(null);
                    }} className={`p-4 rounded-lg border-2 text-sm font-medium transition-all ${calendarSelectedDate === date ? 'border-blue-600 bg-blue-50 text-blue-600 shadow-sm' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2126">
                              <div className="text-xs text-gray-500 mb-1" data-id="element-2127">
                                {new Date(date).toLocaleDateString('en-US', {
                          weekday: 'short'
                        })}
                              </div>
                              <div className="font-bold" data-id="element-2128">
                                {new Date(date).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        })}
                              </div>
                            </button>)}
                        </div>
                      </div>

                      {/* Calendar Grid */}
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2129">
                        <Card padding="lg" className="lg:col-span-2" data-id="element-2130">
                          <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-2131">
                            {new Date(calendarSelectedDate).toLocaleDateString('en-US', {
                        weekday: 'long',
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                          </h3>

                          <div className="grid grid-cols-4 gap-2" data-id="element-2132">
                            {timeSlots.map(slot => {
                        const meeting = getSlotMeeting(calendarSelectedDate, slot);
                        if (meeting) {
                          return <button key={slot} onClick={() => {
                            setCalendarSelectedSlot(slot);
                            setCalendarSelectedMeeting(meeting);
                          }} className={`p-3 rounded-lg text-sm font-medium transition-all ${calendarSelectedSlot === slot ? 'ring-2 ring-offset-2 ring-blue-600' : ''} ${meeting.status === 'confirmed' ? 'bg-green-100 text-green-700 border-2 border-green-300 hover:bg-green-200' : 'bg-yellow-100 text-yellow-700 border-2 border-yellow-300 hover:bg-yellow-200'}`} data-id="element-2133">
                                    {slot}
                                  </button>;
                        }
                        return <button key={slot} onClick={() => {
                          setCalendarSelectedSlot(slot);
                          setCalendarSelectedMeeting(null);
                        }} className={`p-3 rounded-lg text-sm font-medium transition-all ${calendarSelectedSlot === slot ? 'ring-2 ring-blue-600 ring-offset-2 bg-blue-50 text-blue-600' : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'}`} data-id="element-2134">
                                  {slot}
                                </button>;
                      })}
                          </div>

                          <div className="mt-4 flex items-center gap-4 text-xs" data-id="element-2135">
                            <div className="flex items-center gap-2" data-id="element-2136">
                              <div className="w-3 h-3 rounded bg-green-200" data-id="element-2137"></div>
                              <span data-id="element-2138">Confirmed</span>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-2139">
                              <div className="w-3 h-3 rounded bg-yellow-200" data-id="element-2140"></div>
                              <span data-id="element-2141">Pending</span>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-2142">
                              <div className="w-3 h-3 rounded bg-gray-200" data-id="element-2143"></div>
                              <span data-id="element-2144">Available</span>
                            </div>
                          </div>
                        </Card>

                        {/* Meeting Details Panel */}
                        <Card padding="lg" data-id="element-2145">
                          <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-2146">
                            {calendarSelectedMeeting ? 'Meeting Details' : 'Time Slot'}
                          </h3>

                          {calendarSelectedMeeting ? <div className="space-y-4" data-id="element-2147">
                              <div className="flex items-center justify-between" data-id="element-2148">
                                <Badge variant={calendarSelectedMeeting.status === 'confirmed' ? 'success' : 'warning'} size="md" data-id="element-2149">
                                  {calendarSelectedMeeting.status === 'confirmed' ? 'Confirmed' : 'Pending'}
                                </Badge>
                              </div>

                              <div className="flex items-center gap-3" data-id="element-2150">
                                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold flex-shrink-0" data-id="element-2151">
                                  {(calendarSelectedMeeting.participant1Id === participant.id ? calendarSelectedMeeting.participant2Name : calendarSelectedMeeting.participant1Name).split(' ').map(n => n[0]).join('')}
                                </div>
                                <div data-id="element-2152">
                                  <h4 className="font-bold text-gray-900" data-id="element-2153">
                                    {calendarSelectedMeeting.participant1Id === participant.id ? calendarSelectedMeeting.participant2Name : calendarSelectedMeeting.participant1Name}
                                  </h4>
                                </div>
                              </div>

                              <div className="space-y-3 pt-3 border-t border-gray-200" data-id="element-2154">
                                <div className="flex items-start gap-3" data-id="element-2155">
                                  <CalendarIcon className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" data-id="element-2156" />
                                  <div data-id="element-2157">
                                    <p className="text-xs text-gray-500" data-id="element-2158">
                                      Date
                                    </p>
                                    <p className="text-sm font-medium text-gray-900" data-id="element-2159">
                                      {new Date(calendarSelectedMeeting.date).toLocaleDateString('en-US', {
                                weekday: 'long',
                                month: 'long',
                                day: 'numeric'
                              })}
                                    </p>
                                  </div>
                                </div>

                                <div className="flex items-start gap-3" data-id="element-2160">
                                  <ClockIcon className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" data-id="element-2161" />
                                  <div data-id="element-2162">
                                    <p className="text-xs text-gray-500" data-id="element-2163">
                                      Time
                                    </p>
                                    <p className="text-sm font-medium text-gray-900" data-id="element-2164">
                                      {calendarSelectedMeeting.startTime} -{' '}
                                      {calendarSelectedMeeting.endTime}
                                    </p>
                                  </div>
                                </div>

                                <div className="flex items-start gap-3" data-id="element-2165">
                                  <MapPinIcon className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" data-id="element-2166" />
                                  <div data-id="element-2167">
                                    <p className="text-xs text-gray-500" data-id="element-2168">
                                      Location
                                    </p>
                                    <p className="text-sm font-medium text-gray-900" data-id="element-2169">
                                      {calendarSelectedMeeting.location}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div> : calendarSelectedSlot ? <div className="space-y-4" data-id="element-2170">
                              <div className="flex items-center gap-3" data-id="element-2171">
                                <ClockIcon className="w-5 h-5 text-gray-400" data-id="element-2172" />
                                <div data-id="element-2173">
                                  <p className="text-xs text-gray-500" data-id="element-2174">
                                    Selected Time
                                  </p>
                                  <p className="text-sm font-medium text-gray-900" data-id="element-2175">
                                    {calendarSelectedSlot}
                                  </p>
                                </div>
                              </div>

                              <div className="flex items-center gap-3" data-id="element-2176">
                                <CalendarIcon className="w-5 h-5 text-gray-400" data-id="element-2177" />
                                <div data-id="element-2178">
                                  <p className="text-xs text-gray-500" data-id="element-2179">Date</p>
                                  <p className="text-sm font-medium text-gray-900" data-id="element-2180">
                                    {new Date(calendarSelectedDate).toLocaleDateString('en-US', {
                              weekday: 'long',
                              month: 'long',
                              day: 'numeric'
                            })}
                                  </p>
                                </div>
                              </div>

                              <div className="pt-3 border-t border-gray-200" data-id="element-2181">
                                <p className="text-sm text-gray-600 mb-3" data-id="element-2182">
                                  This time slot is available.
                                </p>
                              </div>
                            </div> : <div className="text-center py-8" data-id="element-2183">
                              <CalendarIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" data-id="element-2184" />
                              <p className="text-sm text-gray-500" data-id="element-2185">
                                Select a time slot to view details
                              </p>
                            </div>}
                        </Card>
                      </div>

                      {/* Meetings List */}
                      {getMeetingsForDate(calendarSelectedDate).length > 0 && <Card padding="lg" data-id="element-2186">
                          <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-2187">
                            Scheduled Meetings
                          </h3>
                          <div className="space-y-3" data-id="element-2188">
                            {getMeetingsForDate(calendarSelectedDate).map(meeting => <div key={meeting.id} onClick={() => {
                      setCalendarSelectedSlot(meeting.startTime);
                      setCalendarSelectedMeeting(meeting);
                    }} className={`flex items-start gap-4 p-4 border-2 rounded-lg transition-colors cursor-pointer ${calendarSelectedMeeting?.id === meeting.id ? 'border-blue-300 bg-blue-50' : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'}`} data-id="element-2189">
                                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold flex-shrink-0" data-id="element-2190">
                                    {(meeting.participant1Id === participant.id ? meeting.participant2Name : meeting.participant1Name).split(' ').map(n => n[0]).join('')}
                                  </div>
                                  <div className="flex-1 min-w-0" data-id="element-2191">
                                    <div className="flex items-start justify-between gap-2 mb-1" data-id="element-2192">
                                      <h4 className="font-semibold text-gray-900" data-id="element-2193">
                                        {meeting.participant1Id === participant.id ? meeting.participant2Name : meeting.participant1Name}
                                      </h4>
                                      <Badge variant={meeting.status === 'confirmed' ? 'success' : 'warning'} size="sm" data-id="element-2194">
                                        {meeting.status === 'confirmed' ? 'Confirmed' : 'Pending'}
                                      </Badge>
                                    </div>
                                    <div className="flex items-center gap-3 text-sm text-gray-500" data-id="element-2195">
                                      <div className="flex items-center gap-1" data-id="element-2196">
                                        <ClockIcon className="w-3 h-3" data-id="element-2197" />
                                        {meeting.startTime} - {meeting.endTime}
                                      </div>
                                      <div className="flex items-center gap-1" data-id="element-2198">
                                        <MapPinIcon className="w-3 h-3" data-id="element-2199" />
                                        {meeting.location}
                                      </div>
                                    </div>
                                  </div>
                                </div>)}
                          </div>
                        </Card>}
                    </div>

                    <div className="mt-6 flex gap-3" data-id="element-2200">
                      <Button variant="outline" onClick={() => {
                  setShowCalendarView(false);
                  setViewingParticipantId(null);
                  setCalendarSelectedDate('October 24, 2025');
                  setCalendarSelectedSlot(null);
                  setCalendarSelectedMeeting(null);
                }} className="flex-1" data-id="element-2201">
                        Close
                      </Button>
                      <Button variant="primary" onClick={() => {
                  handleAddToStaging(participant.id);
                  setShowCalendarView(false);
                  setViewingParticipantId(null);
                  setCalendarSelectedDate('October 24, 2025');
                  setCalendarSelectedSlot(null);
                  setCalendarSelectedMeeting(null);
                }} className="flex-1" disabled={stagedParticipants.includes(participant.id)} data-id="element-2202">
                        <UserPlusIcon className="w-4 h-4 mr-2" data-id="element-2203" />
                        Add to Meeting
                      </Button>
                    </div>
                  </>;
          })()}
            </div>
          </Card>
        </div>}

      {/* Compare Calendars Modal */}
      {showCompareCalendar && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-2204">
          <Card className="w-full max-w-6xl" data-id="element-2205">
            <div className="p-6 max-h-[85vh] overflow-y-auto" data-id="element-2206">
              <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-2207">
                Compare Calendars & Schedule Meeting
              </h3>
              <p className="text-sm text-gray-600 mb-6" data-id="element-2208">
                Comparing {stagedParticipants.length} participant
                {stagedParticipants.length !== 1 ? 's' : ''}
              </p>

              {/* Participant List */}
              <div className="mb-6 flex flex-wrap gap-2" data-id="element-2209">
                {stagedParticipants.map(participantId => {
              const participant = participants.find(p => p.id === participantId);
              if (!participant) return null;
              return <div key={participantId} className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg border border-blue-200" data-id="element-2210">
                      <UserIcon className="w-4 h-4 text-blue-600" data-id="element-2211" />
                      <span className="text-sm font-medium text-gray-900" data-id="element-2212">
                        {participant.name}
                      </span>
                    </div>;
            })}
              </div>

              <div className="mb-4" data-id="element-2213">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2214">
                  Select Date
                </label>
                <div className="grid grid-cols-3 gap-2" data-id="element-2215">
                  {eventDates.map(date => <button key={date} onClick={() => {
                setCompareDate(date);
                setSelectedTimeSlot(null);
              }} className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${compareDate === date ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2216">
                      {new Date(date).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric'
                })}
                    </button>)}
                </div>
              </div>

              <div className="mb-6" data-id="element-2217">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2218">
                  Available Time Slots (30-minute slots)
                </label>
                <div className="grid grid-cols-8 gap-2 max-h-96 overflow-y-auto p-2 bg-gray-50 rounded-lg" data-id="element-2219">
                  {timeSlots.map(slot => {
                const availability = getSlotAvailability(compareDate, slot, stagedParticipants);
                const isSelected = selectedTimeSlot === slot;
                return <button key={slot} onClick={() => availability === 'available' && setSelectedTimeSlot(slot)} disabled={availability === 'unavailable'} className={`p-2 rounded text-center text-xs font-medium transition-all ${isSelected ? 'ring-2 ring-blue-600 ring-offset-2' : ''} ${availability === 'unavailable' ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : availability === 'available' ? 'bg-green-100 text-green-700 border-2 border-green-300 hover:bg-green-200 cursor-pointer' : 'bg-yellow-100 text-yellow-700 border-2 border-yellow-300 hover:bg-yellow-200 cursor-pointer'}`} data-id="element-2220">
                        {slot}
                      </button>;
              })}
                </div>

                <div className="mt-4 flex gap-4 text-xs" data-id="element-2221">
                  <div className="flex items-center gap-1" data-id="element-2222">
                    <div className="w-4 h-4 rounded bg-green-300" data-id="element-2223"></div>
                    <span data-id="element-2224">
                      All available ({stagedParticipants.length}/
                      {stagedParticipants.length})
                    </span>
                  </div>
                  <div className="flex items-center gap-1" data-id="element-2225">
                    <div className="w-4 h-4 rounded bg-yellow-300" data-id="element-2226"></div>
                    <span data-id="element-2227">Partial availability</span>
                  </div>
                  <div className="flex items-center gap-1" data-id="element-2228">
                    <div className="w-4 h-4 rounded bg-gray-300" data-id="element-2229"></div>
                    <span data-id="element-2230">Unavailable</span>
                  </div>
                </div>
              </div>

              {selectedTimeSlot && <div className="mb-6" data-id="element-2231">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2232">
                    Meeting Location
                  </label>
                  <Input type="text" placeholder="e.g., Stand A12, Meeting Room 1" value={meetingLocation} onChange={e => setMeetingLocation(e.target.value)} data-id="element-2233" />
                </div>}

              <div className="flex gap-3" data-id="element-2234">
                <Button variant="outline" onClick={() => {
              setShowCompareCalendar(false);
              setSelectedTimeSlot(null);
              setMeetingLocation('');
            }} className="flex-1" data-id="element-2235">
                  Cancel
                </Button>
                <Button variant="primary" className="flex-1" onClick={handleCreateMeeting} disabled={!selectedTimeSlot || !meetingLocation} data-id="element-2236">
                  <CalendarIcon className="w-4 h-4 mr-2" data-id="element-2237" />
                  Schedule Meeting
                </Button>
              </div>
            </div>
          </Card>
        </div>}
    </div>;
}